// src/firebase/auth.js
// ─────────────────────────────────────────────────────────────────────────────
// All Firebase Authentication + Admin role operations
//
// ROLES:
//   super_admin     → full access to everything
//   catalog_manager → add/edit designs, banners, categories (no delete, no settings)
//   viewer          → read-only dashboard
//
// FIRST SUPER ADMIN SETUP (one-time, in Firebase Console):
//   1. Firebase Console → Authentication → Add user → enter email + password
//   2. Copy the UID shown
//   3. Firestore → admins collection → Add document
//      Document ID = that UID
//      Fields: { name: "Your Name", email: "you@email.com", role: "super_admin", active: true, createdAt: now }
// ─────────────────────────────────────────────────────────────────────────────

import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc,
  collection, query, orderBy, serverTimestamp, getDocs, onSnapshot,
} from "firebase/firestore";
import { auth, db } from "./config";

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

// ─── Sign In ──────────────────────────────────────────────────────────────────
export const signInWithEmail = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);

export const signInWithGoogle = async () => {
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error) {
    const shouldFallbackToRedirect = [
      "auth/popup-blocked",
      "auth/operation-not-supported-in-this-environment",
    ].includes(error?.code);

    if (!shouldFallbackToRedirect) throw error;

    await signInWithRedirect(auth, googleProvider);
    return { redirected: true };
  }
};

export const signOutAdmin = () => signOut(auth);

// ─── Auth State ───────────────────────────────────────────────────────────────
export const onAdminAuthState = (cb) =>
  onAuthStateChanged(auth, cb);

// ─── Admin Profile ────────────────────────────────────────────────────────────
// Fetch admin record from Firestore by UID
export const getAdminProfile = async (uid) => {
  const snap = await getDoc(doc(db, "admins", uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
};

// ─── Admin Management (Super Admin only) ──────────────────────────────────────
export const fetchAllAdmins = async () => {
  const q = query(collection(db, "admins"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
};

export const subscribeAllAdmins = (cb) => {
  const q = query(collection(db, "admins"), orderBy("createdAt", "desc"));
  return onSnapshot(q, snap =>
    cb(snap.docs.map(d => ({ uid: d.id, ...d.data() })))
  );
};

// Create admin record in Firestore
// NOTE: Firebase Auth account must be created separately in Firebase Console
// OR use Firebase Admin SDK on backend. Here we create just the Firestore record.
export const createAdminRecord = async (uid, data, createdByUid) => {
  await setDoc(doc(db, "admins", uid), {
    ...data,
    active: true,
    createdBy: createdByUid,
    createdAt: serverTimestamp(),
  });
  await logActivity(createdByUid, "create_admin", `Created admin: ${data.email} (${data.role})`);
};

// Create a representative record in Firestore.
// Representatives do NOT log in (no Firebase Auth UID). They are auto-id'd
// identities used only to assign video calls and receive WhatsApp reminders.
export const createRepresentativeRecord = async (data, createdByUid) => {
  const ref = await addDoc(collection(db, "admins"), {
    ...data,
    role: "representative",
    active: true,
    createdBy: createdByUid,
    createdAt: serverTimestamp(),
  });
  await logActivity(createdByUid, "create_rep", `Created representative: ${data.name}`);
  return ref.id;
};

// Update a representative's details (name / whatsapp phone).
export const updateRepresentative = async (id, data, updatedByUid) => {
  await updateDoc(doc(db, "admins", id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
  await logActivity(updatedByUid, "update_rep", `Updated representative: ${id}`);
};

export const updateAdminRole = async (uid, role, updatedByUid) => {
  await updateDoc(doc(db, "admins", uid), { role, updatedAt: serverTimestamp() });
  await logActivity(updatedByUid, "update_role", `Changed role for ${uid} to ${role}`);
};

export const toggleAdminActive = async (uid, active, updatedByUid) => {
  await updateDoc(doc(db, "admins", uid), { active, updatedAt: serverTimestamp() });
  await logActivity(updatedByUid, active ? "enable_admin" : "disable_admin", `${active ? "Enabled" : "Disabled"} admin: ${uid}`);
};

export const deleteAdminRecord = async (uid, deletedByUid) => {
  await deleteDoc(doc(db, "admins", uid));
  await logActivity(deletedByUid, "delete_admin", `Deleted admin record: ${uid}`);
};

// ─── Password / Email update ──────────────────────────────────────────────────
export const changePassword = async (currentPassword, newPassword) => {
  const user = auth.currentUser;
  const cred = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, cred);
  await updatePassword(user, newPassword);
};

// ─── Activity Log ─────────────────────────────────────────────────────────────
export const logActivity = async (uid, action, detail = "") => {
  try {
    await setDoc(doc(collection(db, "activityLog")), {
      uid, action, detail,
      timestamp: serverTimestamp(),
    });
  } catch (e) { /* never block on logging */ }
};

export const fetchActivityLog = async (limitCount = 50) => {
  const q = query(collection(db, "activityLog"), orderBy("timestamp", "desc"));
  const snap = await getDocs(q);
  return snap.docs.slice(0, limitCount).map(d => ({ id: d.id, ...d.data() }));
};

// ─── Permissions helper ───────────────────────────────────────────────────────
export const PERMISSIONS = {
  super_admin: {
    canManageDesigns:    true,
    canDeleteDesigns:    true,
    canManageBanners:    true,
    canManageCategories: true,
    canManageCustomers:  true,
    canManageQuotations: true,
    canManageAdmins:     true,
    canManageBranding:   true,
    canViewActivity:     true,
  },
  catalog_manager: {
    canManageDesigns:    true,
    canDeleteDesigns:    false,
    canManageBanners:    true,
    canManageCategories: true,
    canManageCustomers:  false,
    canManageQuotations: false,
    canManageAdmins:     false,
    canManageBranding:   false,
    canViewActivity:     false,
  },
  // Representatives don't log in / have no dashboard. They exist only as
  // assignable identities for video calls + receive WhatsApp reminders.
  representative: {
    canManageDesigns:    false,
    canDeleteDesigns:    false,
    canManageBanners:    false,
    canManageCategories: false,
    canManageCustomers:  false,
    canManageQuotations: false,
    canManageAdmins:     false,
    canManageBranding:   false,
    canViewActivity:     false,
  },
  viewer: {
    canManageDesigns:    false,
    canDeleteDesigns:    false,
    canManageBanners:    false,
    canManageCategories: false,
    canManageCustomers:  false,
    canManageQuotations: false,
    canManageAdmins:     false,
    canManageBranding:   false,
    canViewActivity:     false,
  },
};

export const getPermissions = (role) =>
  PERMISSIONS[role] || PERMISSIONS.viewer;

export const ROLE_LABELS = {
  super_admin:     "Super Admin",
  catalog_manager: "Catalog Manager",
  representative:  "Representative",
  viewer:          "Viewer",
};

export const ROLE_COLORS = {
  super_admin:     "#C9971C",
  catalog_manager: "#1E8449",
  representative:  "#E67E22",
  viewer:          "#2196F3",
};